/**
 * Stack of contextual navigation "layers" rendered inside <taco-menu>.
 * Consumers push/pop/update; taco-menu subscribes via context-nav-stack-changed.
 * No work on import beyond defining functions.
 */

export const CONTEXT_NAV_STACK_CHANGED = 'context-nav-stack-changed';

/** @typedef {{ href: string, label: string, icon?: string, badge?: string|null, dataRoute?: string, kind?: 'link'|'heading', indent?: boolean }} ContextNavItem */

/** @typedef {{
 *   ownerId: string,
 *   id: string,
 *   title: string,
 *   subtitle?: string|null,
 *   items: ContextNavItem[],
 * }} ContextNavLayerSpec */

/** @type {ContextNavLayerSpec[]} */
let layers = [];

export function getContextNavDepth() {
  return layers.length;
}

/** @returns {readonly ContextNavLayerSpec[]} */
export function getContextNavLayers() {
  return layers.slice();
}

function normalizeLayer(spec) {
  return {
    ownerId: String(spec.ownerId),
    id: String(spec.id),
    title: spec.title === null || spec.title === undefined ? '' : String(spec.title),
    subtitle: spec.subtitle === null || spec.subtitle === undefined || spec.subtitle === '' ? null : String(spec.subtitle),
    items: Array.isArray(spec.items) ? spec.items : []
  };
}

function notify() {
  document.dispatchEvent(new CustomEvent(CONTEXT_NAV_STACK_CHANGED, {
    bubbles: true,
    detail: {
      depth: layers.length,
      layersSummary: layers.map(l => ({ ownerId: l.ownerId, id: l.id, title: l.title }))
    }
  }));
}

/**
 * Append or replace-by-(ownerId,id) a layer.
 */
export function pushLayer(spec) {
  const layer = normalizeLayer(spec);
  const idx = layers.findIndex(l => l.ownerId === layer.ownerId && l.id === layer.id);
  if (idx >= 0) {
    layers[idx] = layer;
  } else {
    layers.push(layer);
  }
  notify();
}

/**
 * Remove the top layer if it belongs to ownerId; or remove a specific layer anywhere.
 * @returns {boolean} whether something was removed
 */
export function popLayer(ownerId, id = null) {
  if (!layers.length) {
    return false;
  }
  if (id !== null && id !== undefined) {
    const i = layers.findIndex(l => l.ownerId === ownerId && l.id === id);
    if (i < 0) {
      return false;
    }
    layers.splice(i, 1);
    notify();
    return true;
  }
  const top = layers[layers.length - 1];
  if (top.ownerId !== ownerId) {
    return false;
  }
  layers.pop();
  notify();
  return true;
}

/** Remove the innermost layer regardless of owner */
export function popTopLayer() {
  if (!layers.length) {
    return false;
  }
  layers.pop();
  notify();
  return true;
}

/** Remove all layers for an owner (route teardown) */
export function popOwner(ownerId) {
  const before = layers.length;
  layers = layers.filter(l => l.ownerId !== ownerId);
  if (layers.length !== before) {
    notify();
    return true;
  }
  return false;
}

/** @param {Partial<ContextNavLayerSpec>} patch */
export function updateLayer(ownerId, id, patch) {
  const layer = layers.find(l => l.ownerId === ownerId && l.id === id);
  if (!layer) {
    return false;
  }
  if (patch.title !== null && patch.title !== undefined) {
    layer.title = String(patch.title);
  }
  if (patch.subtitle !== undefined) {
    layer.subtitle = patch.subtitle === null || patch.subtitle === undefined || patch.subtitle === '' ? null : String(patch.subtitle);
  }
  if (patch.items !== null && patch.items !== undefined) {
    layer.items = Array.isArray(patch.items) ? patch.items : [];
  }
  notify();
  return true;
}

export function replaceTopLayer(spec) {
  if (!layers.length) {
    pushLayer(spec);
    return true;
  }
  const layer = normalizeLayer(spec);
  layers[layers.length - 1] = layer;
  notify();
  return true;
}

export function clearContextNav() {
  if (!layers.length) {
    return;
  }
  layers = [];
  notify();
}

/**
 * @param {(layers: ContextNavLayerSpec[]) => void} listener
 * @returns {() => void}
 */
export function subscribeContextNavStack(listener) {
  const handler = () => {
    listener(layers.slice());
  };
  document.addEventListener(CONTEXT_NAV_STACK_CHANGED, handler);
  return () => document.removeEventListener(CONTEXT_NAV_STACK_CHANGED, handler);
}
